import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpenIcon,
  QrCodeIcon,
  ChartBarIcon,
  ArrowRightIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { useInView } from "../hooks/useInView";
import { supabase } from "../lib/supabase";

const features = [
  {
    icon: QrCodeIcon,
    title: "Lend in seconds",
    desc: "Scan a borrower card, scan a book. The longest lending line you've ever had is two pupils.",
  },
  {
    icon: BookOpenIcon,
    title: "Catalogue an afternoon of books without typing",
    desc: "Scan the barcode; title, author, and cover appear. Hundreds of books in before the bell rings.",
  },
  {
    icon: ChartBarIcon,
    title: "Know what's getting read",
    desc: "Late books, popular titles, reading activity by year group — one click, printable or CSV.",
  },
];

const steps = [
  {
    num: "01",
    title: "Install & set up",
    desc: "Download Bookburrow, enter your school name, and set a 4-digit admin PIN. You're running in under two minutes.",
  },
  {
    num: "02",
    title: "Add your books",
    desc: "Scan ISBNs with a barcode scanner and watch details fill in automatically. Import borrowers from a CSV.",
  },
  {
    num: "03",
    title: "Start lending",
    desc: "Hand the scanner to a parent helper or TA. Scan borrower, scan book, done. They'll pick it up in five minutes.",
  },
];

export function Landing() {
  const featuresView = useInView<HTMLElement>(0.05);
  const stepsView = useInView<HTMLElement>(0.1);
  const ctaView = useInView<HTMLElement>(0.2);

  return (
    <div className="landing-page">
      {/* ── Hero ────────────────────────────────────────── */}
      <section className="landing-hero grain relative overflow-hidden bg-cream-50">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 pb-16 pt-14 lg:grid-cols-[1fr_1.05fr] lg:items-center lg:gap-16 lg:pb-24 lg:pt-24">
          {/* Copy */}
          <div className="relative z-10 max-w-xl">
            <p className="editorial-label mb-7 flex items-center gap-3 text-forest-700">
              <span aria-hidden="true" className="h-px w-8 bg-forest-500" />
              A little order. A lot more reading.
            </p>
            <h1 className="font-display text-5xl font-medium leading-[1.06] tracking-tight text-forest-950 sm:text-6xl xl:text-7xl">
              Your school library,{" "}
              <em className="text-forest-600">beautifully</em>{" "}
              simple.
            </h1>

            <p className="mt-6 max-w-md text-lg leading-relaxed text-bark-600">
              You already have lessons to plan and marking to finish. Bookburrow
              takes library management off your plate — no training manuals, no
              complicated setups. Just scan&nbsp;and&nbsp;go.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#waitlist"
                className="inline-flex items-center gap-5 rounded-lg bg-forest-800 px-6 py-4 text-sm font-semibold text-cream-50 shadow-lg shadow-forest-950/10 transition-colors hover:bg-forest-700"
              >
                Join the waitlist
                <ArrowRightIcon aria-hidden="true" className="h-4 w-4" />
              </a>
              <Link
                to="/features"
                className="rounded-xl border border-forest-200 bg-white/60 px-6 py-3.5 text-sm font-semibold text-forest-800 transition-all hover:border-forest-300 hover:bg-white"
              >
                See how it works
              </Link>
            </div>

            <p className="mt-6 max-w-sm text-xs leading-relaxed text-bark-600">
              Bookburrow is currently in private beta &mdash; join the waitlist
              and we&rsquo;ll be in touch when we open up.
            </p>
          </div>

          <figure className="library-preview relative min-w-0">
            <div className="mb-6 flex items-center justify-between gap-4 text-forest-800">
              <span className="editorial-label">Inside Bookburrow</span>
              <BookOpenIcon aria-hidden="true" className="h-6 w-6" />
            </div>
            <div className="overflow-hidden rounded-xl border border-forest-950/10 bg-white shadow-xl shadow-forest-950/10">
              <img
                src="/screenshots/add-to-library.png"
                alt="Bookburrow’s Add to Library screen, with a book cover and details filled in from an ISBN lookup."
                width={1703}
                height={1197}
                fetchPriority="high"
                className="block h-auto w-full"
              />
            </div>
            <figcaption className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-forest-800">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-forest-800 text-cream-50">
                <CheckIcon aria-hidden="true" className="h-4 w-4" />
              </span>
              <span><strong className="block font-semibold">One scan. A new story on the shelf.</strong>Title, author and cover, taken care of.</span>
            </figcaption>
          </figure>
        </div>

        <div className="relative mx-auto max-w-7xl px-6">
          <ul className="grid gap-4 border-t border-forest-800/15 py-7 text-sm text-forest-800 sm:grid-cols-3">
            {["Made for busy school days", "Lend and return without Wi-Fi", "One licence. Yours to keep."].map((item) => (
              <li key={item} className="flex items-center gap-3"><CheckIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-forest-600" />{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Features grid ──────────────────────────────── */}
      <section
        ref={featuresView.ref}
        className="relative bg-cream-100 py-24 md:py-32"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-2xl">
            <p className="editorial-label mb-5 text-forest-700">Less admin, more library</p>
            <h2 className={`font-display text-3xl font-semibold tracking-tight text-forest-950 sm:text-4xl reveal ${featuresView.isVisible ? "visible" : ""}`}>
              Built for the way teachers actually{" "}
              <span className="text-forest-500">run libraries.</span>
            </h2>
            <p className={`mt-4 text-lg leading-relaxed text-bark-600 reveal ${featuresView.isVisible ? "visible" : ""}`} style={{ transitionDelay: "100ms" }}>
              Designed for teachers who fit the library around everything else,
              not full-time cataloguers.
            </p>
          </div>

          <div className={`stagger mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3`}>
            {features.map((f) => (
              <div
                key={f.title}
                className={`reveal group rounded-xl border border-forest-800/15 bg-cream-50 p-8 transition-colors hover:border-forest-400 ${featuresView.isVisible ? "visible" : ""}`}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-forest-50 text-forest-600 transition-colors group-hover:bg-forest-100">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-medium leading-tight text-forest-900">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-bark-600">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ───────────────────────────────── */}
      <section
        ref={stepsView.ref}
        className="grain relative overflow-hidden bg-forest-950 py-24 text-cream-100 md:py-32"
      >
        <div className="mx-auto max-w-7xl px-6">
          <p className="editorial-label mb-5 text-gold-300">A simpler school day starts here</p>
          <h2 className={`font-display text-3xl font-semibold tracking-tight sm:text-4xl reveal ${stepsView.isVisible ? "visible" : ""}`}>
            Up and running in an afternoon.
          </h2>

          <div className={`stagger mt-16 grid gap-12 md:grid-cols-3`}>
            {steps.map((step) => (
              <div
                key={step.num}
                className={`reveal ${stepsView.isVisible ? "visible" : ""}`}
              >
                <span className="font-display text-5xl font-medium italic text-gold-300">
                  {step.num}
                </span>
                <h3 className="mt-4 font-display text-xl font-semibold text-cream-100">
                  {step.title}
                </h3>
                <p className="mt-3 leading-relaxed text-cream-300/90">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>

          <figure
            className={`reveal relative mx-auto mt-24 max-w-4xl ${stepsView.isVisible ? "visible" : ""}`}
            style={{ transitionDelay: "250ms" }}
          >
            {/* Plate number, floating in the left margin */}
            <div className="absolute -top-10 left-0 z-10 flex items-baseline gap-3 font-display italic md:-left-32 md:top-2 lg:-left-40">
              <span aria-hidden="true" className="text-5xl text-gold-400 md:text-6xl">№</span>
              <span className="text-xs uppercase tracking-[0.3em] text-gold-300/70">
                A peek
              </span>
            </div>

            {/* Warm paper mount behind the plate */}
            <div className="absolute -inset-2 -rotate-[1.25deg] rounded-2xl bg-cream-100/[0.08] ring-1 ring-cream-100/15 shadow-2xl shadow-forest-950/70" />

            {/* The plate itself */}
            <div className="relative overflow-hidden rounded-2xl border border-gold-400/25 bg-cream-50 shadow-2xl shadow-forest-950/80 ring-1 ring-forest-950/40">
              <img
                src="/screenshots/add-to-library.png"
                alt="Bookburrow's Add to Library wizard: a three-step form labelled Classify, Identify, and Confirm, with an ISBN lookup filling in the book cover and details automatically."
                width={1703}
                height={1197}
                loading="lazy"
                className="block w-full h-auto"
              />
            </div>

            <figcaption className="mt-6 flex items-center justify-center gap-3 font-display italic text-cream-300/60">
              <span aria-hidden="true" className="h-px w-8 bg-gold-400/40" />
              Three short steps: classify, identify, confirm.
              <span aria-hidden="true" className="h-px w-8 bg-gold-400/40" />
            </figcaption>
          </figure>
        </div>

        {/* Decorative gradient orb */}
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-forest-800/30 blur-3xl" />
      </section>

      {/* ── Pricing preview ────────────────────────────── */}
      <section className="bg-cream-50 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <h2 className="font-display text-3xl font-semibold tracking-tight text-forest-950 sm:text-4xl">
            Simple, fair pricing.
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-lg text-bark-600">
            &pound;149 once, with 12 months of updates and support. Renew for
            &pound;49 a year if you want to stay up to date &mdash; or don&rsquo;t,
            and Bookburrow keeps working on the version you have.
          </p>
          <Link
            to="/pricing"
            className="mt-8 inline-block text-sm font-semibold text-forest-600 underline decoration-forest-300 underline-offset-4 transition-colors hover:text-forest-800 hover:decoration-forest-500"
          >
            View pricing details
          </Link>
        </div>
      </section>

      {/* ── Waitlist ─────────────────────────────────────── */}
      <WaitlistSection ctaView={ctaView} />
    </div>
  );
}

interface WaitlistSectionProps {
  ctaView: ReturnType<typeof useInView<HTMLElement>>;
}

function WaitlistSection({ ctaView }: WaitlistSectionProps) {
  const [email, setEmail] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const normalized = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    const { error: rpcError } = await supabase.rpc("add_to_waitlist", {
      p_email: normalized,
      p_school_name: schoolName.trim() || null,
      p_note: null,
    });
    setSubmitting(false);

    if (rpcError) {
      setError("Something went wrong. Please try again in a moment.");
      return;
    }

    setSuccess(true);
  }

  return (
    <section
      id="waitlist"
      ref={ctaView.ref}
      className="grain relative overflow-hidden bg-forest-900 py-24 text-cream-100 md:py-32"
    >
      <div
        className={`relative z-10 mx-auto max-w-xl px-6 text-center reveal ${ctaView.isVisible ? "visible" : ""}`}
      >
        <span className="text-sm font-semibold uppercase tracking-widest text-gold-300">
          Private beta
        </span>
        <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
          Join the waitlist.
        </h2>
        <p className="mx-auto mt-6 max-w-md text-lg leading-relaxed text-cream-300/80">
          Bookburrow is currently invite-only while we work with a small group
          of schools. Leave your details and we&rsquo;ll be in touch when we
          open up.
        </p>

        {success ? (
          <div className="mt-10 rounded-2xl bg-cream-100/10 px-6 py-6 text-cream-100 ring-1 ring-cream-100/20">
            <p className="font-display text-xl font-semibold">
              You&rsquo;re on the list.
            </p>
            <p className="mt-2 text-sm text-cream-300/80">
              Thanks &mdash; we&rsquo;ll email{" "}
              <span className="font-medium text-cream-100">
                {email.trim().toLowerCase()}
              </span>{" "}
              when we&rsquo;re ready for you.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-10 flex flex-col gap-3 text-left"
          >
            <label className="text-sm font-medium text-cream-200">
              Email address
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.sch.uk"
                required
                className="mt-1.5 w-full rounded-xl border border-cream-200/20 bg-forest-950/40 px-4 py-3 text-base text-cream-100 placeholder:text-cream-300/40 focus:border-gold-400 focus:outline-none"
              />
            </label>
            <label className="text-sm font-medium text-cream-200">
              School name <span className="text-cream-300/60">(optional)</span>
              <input
                type="text"
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                placeholder="e.g. Oakwood Primary School"
                className="mt-1.5 w-full rounded-xl border border-cream-200/20 bg-forest-950/40 px-4 py-3 text-base text-cream-100 placeholder:text-cream-300/40 focus:border-gold-400 focus:outline-none"
              />
            </label>

            {error && (
              <p className="text-sm text-gold-300">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-2 rounded-xl bg-gold-500 px-6 py-4 text-base font-bold text-bark-900 shadow-lg shadow-gold-500/20 transition-all hover:bg-gold-400 hover:shadow-xl disabled:opacity-60"
            >
              {submitting ? "Adding you..." : "Join the waitlist"}
            </button>
            <p className="mt-2 text-center text-xs text-cream-300/60">
              No spam. We&rsquo;ll only email you once, when we&rsquo;re ready.
            </p>
          </form>
        )}
      </div>

      <div className="absolute -left-40 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full bg-forest-700/20 blur-3xl" />
      <div className="absolute -right-40 top-1/4 h-96 w-96 rounded-full bg-gold-500/5 blur-3xl" />
    </section>
  );
}
